/**
 * Copy and structure for /products landing and detail pages.
 * Slugs must match productLoaders keys and routes.
 */

/** Inbox for the /products overview (not tied to one SKU). */
export const PRODUCTS_OVERVIEW_EMAIL = "products@jamilglobal.com";

/** Pattern: {slug}@jamilglobal.com — e.g. ilmora@jamilglobal.com */
export function productContactEmail(slug) {
  const s = String(slug || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "");
  if (!s) return PRODUCTS_OVERVIEW_EMAIL;
  return `${s}@jamilglobal.com`;
}

export const productLandingItems = [
  {
    slug: "manzil",
    title: "Manzil",
    subtitle: "Kuwait delivery operations platform",
    description:
      "A multi-sided delivery OS for Kuwait restaurants and fleets: admin command center, merchant portal, driver app (web + native), and fleet-company portal—orders, KWD wallets, live PostGIS tracking, and bilingual EN/AR.",
    path: "/products/manzil",
    thumb: "/pictures/case-studies/manzil/02-admin-live-map.webp",
    contactEmail: productContactEmail("manzil"),
    tags: ["4 apps", "Kuwait", "Live dispatch"],
  },
  {
    slug: "ilmora",
    title: "Ilmora",
    subtitle: "AI learning infrastructure your institution owns",
    description:
      "Not another generic EdTech subscription—Ilmora is designed, deployed, and tailored by Jamil Global for each school, college, or university: white-label, fully custom, with clear operational handover so the platform is unmistakably yours.",
    path: "/products/ilmora",
    thumb: "/pictures/case-studies/ilmora/01-marketing-hero.webp",
    externalUrl: "https://ilmora.jamilglobal.com/",
    externalLabel: "Visit product site",
    contactEmail: productContactEmail("ilmora"),
    tags: ["White label", "Bespoke deployment", "Full handover"],
  },
  {
    slug: "mehfil-media",
    title: "Mehfil Media",
    subtitle: "AI UGC for Pakistani brands",
    description:
      "Upload a product photo, cast an AI creator from the Mehfil house, and generate UGC-style Reel campaigns—desi hooks, Urdu-aware scripts, and campaign packs in ~48 hours. Live at mehfil-media.jamilglobal.com.",
    path: "/products/mehfil-media",
    thumb: "/pictures/case-studies/mehfil-media/07-work-gallery.webp",
    externalUrl: "https://mehfil-media.jamilglobal.com/",
    externalLabel: "Visit product site",
    contactEmail: productContactEmail("mehfil-media"),
    tags: ["AI UGC SaaS", "Pakistan", "Photo → video"],
  },
  {
    slug: "easy-growth-ops",
    title: "Easy Growth Ops",
    subtitle: "Pay-per-call profit in Slack",
    description:
      "Two Slack-delivered tools for performance marketers. ROI posts call payout from Ringba, CallGrid, TrackDrive, Retreaver, or Phonexa next to Meta, Google, and TikTok ad spend every 15–30 minutes, depending on setup. Drops sends yesterday's winning ads each morning.",
    path: "https://www.geteasygrowthops.co/",
    externalUrl: "https://www.geteasygrowthops.co/",
    externalLabel: "Visit Easy Growth Ops",
    // No on-site detail page — product lives at geteasygrowthops.co.
    externalOnly: true,
    tags: ["Slack P&L", "Pay-per-call", "Ad intel"],
  },
];

export const productDetailConfigs = {
  manzil: {
    screens: [
      {
        src: "/pictures/case-studies/manzil/02-admin-live-map.webp",
        caption: "Live map — active orders across Kuwait City",
      },
      {
        src: "/pictures/case-studies/manzil/03-admin-orders-kanban.webp",
        caption: "Dispatch board — orders from dispatching to delivered",
      },
      {
        src: "/pictures/case-studies/manzil/13b-merchant-dashboard-ar.webp",
        caption: "Merchant sign-in in Arabic — every surface ships EN/AR",
      },
    ],
    heroEyebrow: "Jamil Global product",
    heroTitle: "Manzil",
    heroTitleAccent: "Kuwait delivery operations platform",
    heroLead:
      "Manzil is a multi-sided delivery management system for Kuwait restaurants and fleets: merchants place and track orders, admins run live dispatch and maps, drivers run pickup-to-POD shifts, and delivery companies manage their riders—with KWD wallets, real-time chat, and bilingual EN/AR surfaces.",
    caseStudyPath: "/case-studies/product-engineering/manzil-delivery-platform",
    caseStudyLabel: "View case study",
    contactEmail: productContactEmail("manzil"),
    bodyLabel: "Why Manzil",
    capabilitiesTitle: "Four coordinated surfaces",
    capabilitiesLead:
      "Admin, merchant, driver, and fleet-company apps on one realtime stack—built for Kuwait addresses, cash, and language.",
    capabilitiesNum: "SEC. 01 — SURFACES",
    contactDescription:
      "Scope Manzil for restaurants, dark kitchens, or delivery fleets in Kuwait—dispatch, wallets, and bilingual rollout. Reach the product team directly.",
    ctaHeadingEm: "your fleet.",
    ctaSteps: [
      {
        title: "Discovery",
        description:
          "Map merchant, ops, driver, and fleet workflows—and Kuwait address and payment constraints.",
        time: "1 week",
      },
      {
        title: "Build the four surfaces",
        description:
          "Ship admin, merchant, driver, and company apps on one realtime API.",
        time: "6–10 weeks",
      },
      {
        title: "Launch & operate",
        description:
          "Dispatch, wallets, and maps in production—with training and handover.",
        time: "Ongoing",
      },
    ],
    stats: [
      {
        label: "Surfaces",
        value: "4 apps",
        subtext: "Admin · merchant · driver · fleet",
      },
      {
        label: "Locale",
        value: "Kuwait-ready",
        subtext: "Addresses · KWD · EN/AR RTL",
      },
      {
        label: "Ops",
        value: "Realtime",
        subtext: "Map + dispatch + chat",
      },
      {
        label: "Payments",
        value: "Wallet",
        subtext: "MyFatoorah top-up + fees",
      },
    ],
    highlight: {
      metric: "One stack. Four roles.",
      description:
        "Replace WhatsApp, spreadsheets, and shared admin logins with role-scoped portals that stay in sync—live map, offer-based dispatch, prepaid merchant wallets, and proof-of-delivery on the driver shift.",
    },
    bodyParagraphs: [
      "Kuwait restaurants needed more than a dispatch phone number. They needed structured order intake, prepaid delivery billing, branch-aware operations, and visibility into who is picking up and when. Platform operators needed a live map and assignment board that works when auto-dispatch fails.",
      "Drivers get a shift tool that opens native directions and unlocks customer details only after verified pickup. Fleet partners get a portal that is not the admin console—scoped map, driver management, and payout windows on the same Node / PostGIS / Socket.io backbone.",
    ],
    capabilities: [
      {
        title: "Admin command center",
        description:
          "Live map, order kanban, drivers, vendors, fleets, invoices, chat, and reports.",
      },
      {
        title: "Merchant portal",
        description:
          "Create orders, wallet top-up, team roles, support chat, and pickup verification.",
      },
      {
        title: "Driver app",
        description:
          "Web + React Native: online status, timed offers, directions, cash, and POD photos.",
      },
      {
        title: "Fleet company portal",
        description:
          "Company-scoped live map, driver CRUD, payout windows, and support—without admin credentials.",
      },
      {
        title: "PostGIS live map",
        description:
          "Spatial tracking for drivers, restaurants, and destinations in one dispatch view.",
      },
      {
        title: "Socket.io dispatch",
        description:
          "Realtime rooms for offers, presence, chat, and map updates across roles.",
      },
      {
        title: "KWD wallet ops",
        description:
          "Prepaid merchant balances with fee deductions, ledgers, and MyFatoorah top-up.",
      },
      {
        title: "EN/AR RTL",
        description:
          "Full bilingual UX with RTL across the web portals for Kuwait teams.",
      },
    ],
    ctaServiceType: "Manzil delivery platform",
  },
  ilmora: {
    screens: [
      {
        src: "/pictures/case-studies/ilmora/01-marketing-hero.webp",
        caption: "Institution-branded front door",
      },
      {
        src: "/pictures/case-studies/ilmora/30-student-home.webp",
        caption: "Student home — timetable, tests, and AI tutor",
      },
      {
        src: "/pictures/case-studies/ilmora/22-teacher-chat.webp",
        caption: "Teacher AI assistant with lesson prompts",
      },
    ],
    heroEyebrow: "Jamil Global product",
    heroTitle: "Ilmora",
    heroTitleAccent: "AI learning infrastructure, owned by your institution",
    heroLead:
      "Ilmora is not a one-size SaaS on shared infrastructure. We work with leadership and IT to scope branding, policies, integrations, and training—then deliver a bespoke deployment with documentation and ownership handed to you. From K–12 through higher education.",
    externalSite: {
      url: "https://ilmora.jamilglobal.com/",
      label: "Explore here",
    },
    contactEmail: productContactEmail("ilmora"),
    bodyLabel: "Why bespoke",
    capabilitiesTitle: "What we configure",
    capabilitiesLead:
      "Example modules Jamil Global tailors per institution—workflows, branding, and integrations follow your governance.",
    capabilitiesNum: "SEC. 01 — MODULES",
    contactDescription:
      "Scope Ilmora for your institution—pricing, white-label rollout, and integrations. Reach the product team directly.",
    ctaHeadingEm: "your campus.",
    ctaSteps: [
      {
        title: "Discovery",
        description:
          "Campus goals, IT constraints, academic policies, and integration reality.",
        time: "Working sessions",
      },
      {
        title: "Design & pilot",
        description:
          "Branding, roles, modules, success metrics, staged rollout.",
        time: "Scoped with you",
      },
      {
        title: "Deploy & hand over",
        description:
          "Training, documentation, and transition to your operators.",
        time: "Project-based",
      },
    ],
    stats: [
      {
        label: "Model",
        value: "Bespoke",
        subtext: "Scope per campus or chain",
      },
      {
        label: "Experience",
        value: "White label",
        subtext: "Your brand, domains, comms",
      },
      {
        label: "Reach",
        value: "K–12 → HE",
        subtext: "Schools, colleges, universities",
      },
      {
        label: "Handover",
        value: "Full",
        subtext: "Operational ownership to you",
      },
    ],
    highlight: {
      metric: "Institution-owned",
      description:
        "You get a clear handover—custom deployment, runbooks, and training—so Ilmora runs as your asset, not a rental capped by someone else's roadmap.",
    },
    bodyParagraphs: [
      "A bespoke solution means we align with your governance, academic model, and timeline—not a rigid product map. Modules, workflows, and integrations are shaped around how your campus already operates, with role-based access and school-scoped data so the right people see the right information.",
      "Students get help that matches how they learn: guided explanations, practice assessments, assignments with structured feedback, and progress views. Teachers gain an AI copilot for curriculum planning, varied assessments, lesson outlines, and grading assistance—scoped so AI assists educators rather than replacing professional judgment.",
    ],
    capabilities: [
      {
        title: "AI tutor chat",
        description:
          "Personalized explanations at the right level—with examples that encourage understanding, not just quick answers.",
      },
      {
        title: "Teacher copilot",
        description:
          "Support for curriculum planning, assessment ideas, rubrics, and day-to-day teaching tasks your policies allow.",
      },
      {
        title: "Smart assessments",
        description:
          "From multiple choice to essays—generated and tuned by topic, grade, and difficulty, with controls you approve.",
      },
      {
        title: "Lesson & slide support",
        description:
          "Structured outlines and presentation-style suggestions for chapters or topics you define.",
      },
      {
        title: "Multi-role platform",
        description:
          "Dedicated flows for administrators, teachers, and students—the tools each role needs in one coherent system.",
      },
      {
        title: "Progress insight",
        description:
          "Period results, engagement signals, and visibility into how classes and cohorts are performing.",
      },
      {
        title: "Secure & private",
        description:
          "Role-based access and institution-scoped data boundaries designed for academic and regulatory expectations.",
      },
      {
        title: "Delivery & handover",
        description:
          "Implementation, training, and transition to your team so the platform is operable as your long-term asset.",
      },
    ],
    ctaServiceType: "Ilmora education platform",
  },
  "mehfil-media": {
    screens: [
      {
        src: "/pictures/case-studies/mehfil-media/07-work-gallery.webp",
        caption: "Generated Reels — every frame is synthetic",
      },
      {
        src: "/pictures/case-studies/mehfil-media/14-campaign-builder.webp",
        caption: "Campaign builder — creator, product, scene, script",
      },
      {
        src: "/pictures/case-studies/mehfil-media/12-dashboard.webp",
        caption: "Brand dashboard — credits, campaigns, delivered videos",
      },
    ],
    heroEyebrow: "Jamil Global product",
    heroTitle: "Mehfil Media",
    heroTitleAccent: "AI UGC for Pakistani brands",
    heroLead:
      "Mehfil Media is an AI content house SaaS: brands upload a product photo, cast a creator from the Pakistani AI roster, and get UGC-style Reel campaigns—hooks, desi idiom, and multi-cut packs—without a shoot day. Live at mehfil-media.jamilglobal.com.",
    externalSite: {
      url: "https://mehfil-media.jamilglobal.com/",
      label: "Explore here",
    },
    contactEmail: productContactEmail("mehfil-media"),
    bodyLabel: "Why Mehfil",
    capabilitiesTitle: "What the product does",
    capabilitiesLead:
      "The SaaS loop and creative system brands use to turn product photos into UGC campaigns for Pakistan.",
    capabilitiesNum: "SEC. 01 — CAPABILITIES",
    contactDescription:
      "Scope Mehfil Media for your brand or agency—campaign packs, creator casting, and rollout. Reach the product team directly.",
    ctaHeadingEm: "your brand.",
    ctaSteps: [
      {
        title: "Brand & shelf map",
        description:
          "Categories, creator fit, pack sizes, and how you want Reels to ship.",
        time: "Working sessions",
      },
      {
        title: "Pilot campaign",
        description:
          "Upload products, cast creators, generate packs, and review performance.",
        time: "Scoped with you",
      },
      {
        title: "Scale the calendar",
        description:
          "Ongoing drops, more SKUs, and always-on UGC without shoot logistics.",
        time: "Ongoing",
      },
    ],
    stats: [
      {
        label: "Loop",
        value: "Upload → cast",
        subtext: "→ generate video",
      },
      {
        label: "Turnaround",
        value: "~48h",
        subtext: "Photo to campaign pack",
      },
      {
        label: "Output",
        value: "30+",
        subtext: "Assets / cuts per product",
      },
      {
        label: "Market",
        value: "Pakistan",
        subtext: "Desi shelves & speech",
      },
    ],
    highlight: {
      metric: "No shoot. No wait.",
      description:
        "One product photo is enough. Mehfil casts an AI-born Pakistani creator, writes market-native hooks, and generates the performance—so brands ship Reels on drop calendars instead of booking studios.",
    },
    bodyParagraphs: [
      "The product path is intentionally simple: upload the SKU image, choose a creator from the Mehfil house, and trigger generation. Scripts lean on desi idiom and Urdu-aware beats; category worlds cover chai, beauty, lawn, jewelry, skincare, fragrance, and more.",
      "The public site sells proof as well as process—synthetic Reel receipts, a five-step how-it-works narrative, and a free first-concept CTA—so brands understand the SaaS before they ever open the uploader.",
    ],
    capabilities: [
      {
        title: "Product image intake",
        description:
          "A phone packshot seeds the whole campaign—no studio logistics required.",
      },
      {
        title: "AI creator casting",
        description:
          "Pick a Pakistani face from the Mehfil roster so audiences trust the performance.",
      },
      {
        title: "Script & hook engine",
        description:
          "Auto-written hooks and CTAs tuned to how the market actually talks—including Urdu beats.",
      },
      {
        title: "UGC video generation",
        description:
          "Synthetic lighting, voice, and gesture—creator-style Reels frame by frame.",
      },
      {
        title: "Campaign packs",
        description:
          "Dozens of cuts, ratios, and hooks for organic posting and paid testing.",
      },
      {
        title: "Category worlds",
        description:
          "Creative patterns for chai, beauty, clothing, jewelry, skincare, fragrance, ice cream, and more.",
      },
      {
        title: "Conversion front end",
        description:
          "High-craft marketing site with performance receipts and clear CTAs into creation.",
      },
      {
        title: "Live product surface",
        description:
          "Canonical experience at mehfil-media.jamilglobal.com—brand, work, and intake narrative in one place.",
      },
    ],
    ctaServiceType: "Mehfil Media AI UGC",
  },
};

export function getProductDetailConfig(slug) {
  return productDetailConfigs[slug] ?? null;
}
